import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Target, 
  Lightbulb, 
  Beaker,
  Recycle,
  TreePine,
  Factory,
  Leaf,
  Building2
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none mb-4">
              <Building2 className="w-3 h-3 mr-1" />
              About Punarvibes
            </Badge>
            <h1 className="text-5xl font-bold text-[#2C3E2F] mb-6">
              Who We Are
            </h1>
            <p className="text-xl text-[#5A6B5E] max-w-3xl mx-auto leading-relaxed mb-2">
              Building the next generation of circular materials.
            </p>
            <p className="text-xl text-[#5A6B5E] max-w-3xl mx-auto leading-relaxed">
              Inspired by nature. Rooted in science.
            </p>
          </div>
        </div>
      </section>

      {/* Our Journey Section */}
      <section className="py-12 px-6 bg-[#FDFBF7]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-[#2C3E2F] mb-6">Our Journey</h2>
          <p className="text-lg text-[#5A6B5E] leading-relaxed mb-6">
            Founded in June 2025 and based in Bengaluru, India, Punarvibes was built on a simple 
            but powerful belief: nature-inspired technologies can transform waste into low-carbon, 
            high-performance materials.
          </p>
          <p className="text-lg text-[#5A6B5E] leading-relaxed">
            Punarvibes Pvt Ltd represents a new generation of climate-tech enterprises. Incubated 
            at the Bangalore Bio-Innovation Center, we focus on biotechnology-driven research and 
            material innovation to develop circular alternatives for modern industries.
          </p>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          {/* Mission, Vision, Approach Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-lg flex items-center justify-center mb-4">
                  <Lightbulb className="w-6 h-6 text-[#D4A574]" />
                </div>
                <CardTitle className="text-[#2C3E2F]">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#5A6B5E]">
                  To enable a shared future where circular practices reach every office and household, 
                  and materials built at scale return safely to the earth.
                </p>
              </CardContent>
            </Card>

            <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#4A5D4E]" />
                </div>
                <CardTitle className="text-[#2C3E2F]">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#5A6B5E]">
                  Create sustainable pathways that transform waste streams into biodegradable products 
                  supporting a circular economy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 px-6 bg-[#FDFBF7]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2C3E2F] mb-6">Our Philosophy</h2>
            <div className="space-y-6 text-lg text-[#5A6B5E]">
              <p className="leading-relaxed">
                <span className="font-semibold text-[#D4A574]">Waste is misplaced resource.</span><br />
                Every day, urban areas generate massive amounts of agricultural residues, 
                textile waste, and organic materials that end up in landfills. We see these 
                not as waste, but as valuable feedstock for tomorrow's materials.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-[#D4A574]">Biology is the most advanced materials lab on Earth.</span><br />
                Nature has perfected material creation over billions of years. By leveraging 
                biological processes like mycelium growth and natural binding systems, we create 
                materials that are both high-performing and completely biodegradable.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-[#D4A574]">Circularity must be local, scalable, and economically viable.</span><br />
                True sustainability isn't just environmental—it's economic. We build circular 
                supply chains that work within local urban ecosystems, creating value while 
                reducing environmental impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section id="team" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-[#2C3E2F] mb-12 text-center">Our Team</h2>
          
          {/* Founders */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-[#D4A574] mb-8">Founders</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Rishov Biswas */}
              <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/n9lqg2mi_Rishov%20Biswas.jpg" 
                      alt="Rishov Biswas" 
                      className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-[#A8C9A5]"
                    />
                    <h4 className="text-xl font-bold text-[#2C3E2F] mb-1">Rishov Biswas</h4>
                    <p className="text-[#D4A574] font-semibold mb-2">Co-Founder & CEO</p>
                    <p className="text-sm text-[#5A6B5E] mb-4">MBA, XLRI Jamshedpur | B.Tech, IIIT Kolkata</p>
                    <p className="text-[#5A6B5E] text-left">
                      Rishov brings over a decade of leadership experience across high-growth startups and 
                      established enterprises. Driven by a deep-rooted interest in the natural world, ecology, 
                      and sustainable systems, he founded Punarvibes in 2025. He drives strategic partnerships 
                      and defines the company's technical roadmap, overseeing experimental programs, growth 
                      protocols, and R&D validation efforts.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Shreya Sinha */}
              <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/za0jj9cd_Shreya%20Sinha.jpg" 
                      alt="Shreya Sinha" 
                      className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-[#A8C9A5]"
                    />
                    <h4 className="text-xl font-bold text-[#2C3E2F] mb-1">Shreya Sinha</h4>
                    <p className="text-[#D4A574] font-semibold mb-2">Co-Founder</p>
                    <p className="text-sm text-[#5A6B5E] mb-4">MBA, SIBM Pune | B.Tech, NIT Kurukshetra</p>
                    <p className="text-[#5A6B5E] text-left">
                      Shreya brings close to two decades of leadership experience across large-scale technology 
                      organizations, where she has built and scaled operational systems. Her passion for 
                      sustainability and circular practices led her to co-found Punarvibes. She leads market 
                      entry and pilot execution, operations and scale planning, and ensures regulatory alignment 
                      and compliance.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Research & Development */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-[#D4A574] mb-8">Research & Development</h3>
            <div className="flex justify-center">
              <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow max-w-md w-full">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center">
                    <img 
                      src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/khefn56y_thejasri%20photo.jpeg" 
                      alt="Thejasri" 
                      className="w-36 h-36 rounded-full object-cover mb-5 border-4 border-[#A8C9A5]"
                    />
                    <h4 className="text-xl font-bold text-[#2C3E2F] mb-1">Thejasri KP</h4>
                    <p className="text-[#D4A574] font-semibold mb-3">Research Associate</p>
                    <p className="text-[#5A6B5E]">
                      Thejasri supports our end-to-end product development and R&D efforts. 
                      Before joining Punarvibes, Thejasri has worked as a Research Associate at 
                      ICAR-CPCRI, Kasaragod, Kerala, specializing in Plant Pathology with a focus 
                      on fungal pathogens.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Scientific & Technical Advisors */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-[#D4A574] mb-8">Scientific & Technical Advisors</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Dr. Saravanan Ravi */}
              <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/cvkkqiwv_saravanan%20Ravi.png" 
                      alt="Dr. Saravanan Ravi" 
                      className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-[#A8C9A5]"
                    />
                    <h4 className="text-xl font-bold text-[#2C3E2F] mb-1">Dr. Saravanan Ravi</h4>
                    <p className="text-[#D4A574] font-semibold mb-2">Mycology & Fungal Biology</p>
                    <p className="text-sm text-[#5A6B5E] mb-4">PhD (Plant Pathology & Mycology) | Bangalore Bio-Innovation Center</p>
                    <p className="text-[#5A6B5E] text-left">
                      Dr. Saravanan provides scientific mentorship and technical guidance on fungal systems, 
                      growth protocols, and experimental direction.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Anshuman Sharma */}
              <Card className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/0eduuzjt_Anshuman%20Sharma_cropped.jpg" 
                      alt="Anshuman Sharma" 
                      className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-[#A8C9A5]"
                    />
                    <h4 className="text-xl font-bold text-[#2C3E2F] mb-1">Anshuman Sharma</h4>
                    <p className="text-[#D4A574] font-semibold mb-2">Materials Science</p>
                    <p className="text-sm text-[#5A6B5E] mb-4">IIT Roorkee | University of Wisconsin | AO Smith</p>
                    <p className="text-[#5A6B5E] text-left">
                      Anshuman advises on materials engineering, structural performance assessment, 
                      and applied validation pathways.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 px-6 bg-[#4A5D4E] text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">
              We Operate at the Intersection Of
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              'Circular Economy',
              'Biomaterials',
              'Urban Waste Valorization',
              'Climate-Conscious Innovation'
            ].map((item) => (
              <div key={item} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-colors">
                <h3 className="text-lg font-semibold">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
